// ONE way to put a date or a timestamp on a screen.
//
// The office had three formats visible on a single wizard step: an ISO
// `2026-07-15` printed as prose, a native `<input type="date">` showing
// `dd/mm/yyyy`, and an imports table rendering `8/10/2026, 1:33:23 PM` from a
// bare `new Date(x).toLocaleString()` with no locale argument — US month-first,
// 12-hour, in a Russian-speaking factory (UX-1 surface review F12). A bare
// `toLocaleString()` also ignores the app's language entirely: it reads the
// browser's, so the same screen disagrees with itself the moment a user
// switches locale.
//
// These two functions are the canonical implementations, MOVED here from
// areas/office/production/lib/format.ts (which now re-exports them, so the
// module-04/05 call sites are untouched and their walkthroughs unaffected).
// New code imports from here.
//
// The `<input type="date">` widget's own display format is decided by the
// browser from the OS locale and is not stylable or overridable — the one
// format on the screen this module cannot pin. That is why every date the app
// itself RENDERS goes through `formatBusinessDate`: the readable text beside an
// input is ours, and it is consistent.

import { facilityTodayIso, todayIso } from "./business-dates";
import { cutBefore, oneRun, type TwoRuns } from "./two-runs";

/** The `YYYY-MM-DD` parts of a business date, or `undefined` if it is not one. */
function businessDateParts(
  dateText: string | null | undefined,
): { y: number; m: number; d: number } | undefined {
  if (dateText === null || dateText === undefined) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dateText.trim());
  if (match === null) return undefined;
  const [, y, m, d] = match;
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return { y: Number(y), m: Number(m), d: Number(d) };
}

/**
 * Render a business DATE (`due_date`, `target_go_live_date`) from its own parts
 * with the formatter pinned to UTC — `new Date("2026-08-05")` parses as UTC
 * midnight and shifts a calendar day for anyone at a negative offset (the
 * module-06 §9.8 lesson).
 */
export function formatBusinessDate(dateText: string | null | undefined, locale?: string): string {
  const parts = businessDateParts(dateText);
  if (parts === undefined) return dateText ?? "";
  const stamp = Date.UTC(parts.y, parts.m - 1, parts.d);
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: "UTC", dateStyle: "medium" }).format(stamp);
  } catch {
    return dateText ?? "";
  }
}

/**
 * A wire timestamp for humans (`reported_at`, `entered_at`, `created_at`).
 *
 * `timeZone` is the D-089 rule applied to DISPLAY: a business instant a factory
 * reasons about — a floor tap's `occurred_at` — renders in the FACILITY's
 * clock, never the viewer's, because the two calendars disagree for part of
 * every day whenever the viewer's OS timezone is not the plant's. Falls back to
 * the viewer's clock only when no timezone is in hand or the runtime does not
 * know the name.
 */
export function formatTimestamp(
  iso: string | null | undefined,
  locale?: string,
  timeZone?: string | null,
): string {
  if (iso === null || iso === undefined) return "";
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return iso;
  const options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
  if (typeof timeZone === "string" && timeZone.length > 0) {
    try {
      return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(stamp);
    } catch {
      // Unknown timezone name — fall through to the viewer's clock.
    }
  }
  try {
    return new Intl.DateTimeFormat(locale, options).format(stamp);
  } catch {
    return iso;
  }
}

/**
 * The calendar date (`YYYY-MM-DD`) a wire timestamp falls on, in the same
 * clock `formatTimestamp` prints it in: the named zone when one is in hand,
 * the viewer's otherwise. Use it wherever a screen groups or labels instants
 * by day. `iso.slice(0, 10)` is the UTC date, which disagrees with the
 * printed time for part of every day east or west of Greenwich: the inbox
 * thread drew "14 Oct" above messages stamped "15 Oct, 08:12" for its first
 * five hours in Almaty (D-600). Answers `undefined` for a value that is not a
 * timestamp.
 */
export function timestampDayIso(
  iso: string | null | undefined,
  timeZone?: string | null,
): string | undefined {
  if (iso === null || iso === undefined) return undefined;
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return undefined;
  if (typeof timeZone === "string" && timeZone.length > 0) return facilityTodayIso(stamp, timeZone);
  return todayIso(new Date(stamp));
}

/**
 * A wire timestamp shown as a date alone ("15 Oct 2026"), in the same clock
 * `formatTimestamp` would print its time in. For a column or a label that
 * names the day something happened (created, issued, released), where
 * cutting the wire value to ten characters printed the UTC date as raw ISO text (D-600).
 */
export function formatTimestampDate(
  iso: string | null | undefined,
  locale?: string,
  timeZone?: string | null,
): string {
  const day = timestampDayIso(iso, timeZone);
  return day === undefined ? (iso ?? "") : formatBusinessDate(day, locale);
}

const DATE_PART_TYPES: ReadonlySet<string> = new Set([
  "era",
  "year",
  "relatedYear",
  "yearName",
  "month",
  "day",
  "weekday",
]);
const TIME_PART_TYPES: ReadonlySet<string> = new Set(["hour", "minute", "second", "dayPeriod"]);

/**
 * `formatTimestamp` cut where the date ends and the time begins (`TwoRuns`,
 * lib/two-runs.ts), so a narrow table cell can put the time under the date
 * while keeping each whole (design-system §3.3, `fit`): "28 нояб. 2025 г.,"
 * over "00:00", never "2025" over "г., 00:00". The cut is the whitespace
 * before the time's first part, and everything before it, the locale's own
 * joiner included ("г.,", ","), stays with the date. A locale that writes the
 * time first, mixes the two, or puts no whitespace between them comes back as
 * one run, and so does a value `formatTimestamp` would not format.
 */
export function timestampParts(
  iso: string | null | undefined,
  locale?: string,
  timeZone?: string | null,
): TwoRuns {
  const whole = formatTimestamp(iso, locale, timeZone);
  if (iso === null || iso === undefined) return oneRun(whole);
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return oneRun(whole);
  // The formatter `formatTimestamp` used, with its fallbacks. Its parts only
  // locate the cut, which `cutBefore` makes in `whole` itself.
  const options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" };
  let parts: Intl.DateTimeFormatPart[] | undefined;
  if (typeof timeZone === "string" && timeZone.length > 0) {
    try {
      parts = new Intl.DateTimeFormat(locale, { ...options, timeZone }).formatToParts(stamp);
    } catch {
      parts = undefined;
    }
  }
  if (parts === undefined) {
    try {
      parts = new Intl.DateTimeFormat(locale, options).formatToParts(stamp);
    } catch {
      return oneRun(whole);
    }
  }
  const firstTime = parts.findIndex((p) => TIME_PART_TYPES.has(p.type));
  if (firstTime <= 0) return oneRun(whole);
  const before = parts.slice(0, firstTime);
  if (!before.some((p) => DATE_PART_TYPES.has(p.type))) return oneRun(whole);
  if (parts.slice(firstTime).some((p) => DATE_PART_TYPES.has(p.type))) return oneRun(whole);
  const at = before.reduce((length, p) => length + p.value.length, 0);
  return cutBefore(
    whole,
    parts.map((p) => p.value),
    at,
  );
}
